import { useEffect, useRef, useState } from "react";
import { capitalize } from "@mui/material";
import { PER_PAGE_SIZE } from "consts/transactions";
import { Filters } from "consts/paymentMethods";
import { BooleanSwitchValue } from "consts/general";
import { supportedPaymentMethods, SupportedPaymentMethodsTag } from "consts/ecommerce";
import { RequestQueryMapper } from "util/request-query-mapper";
import { when } from "util/support";
import { convertToBooleanSwitch } from "util/transformers";
import { useTypedSelector } from "hooks";
import useFetchResource from "hooks/useFetchResource";
import { getPaymentMethods } from "features/paymentMethods/api";
import { selectIsPaymentMethodsLoading, selectPaymentMethodsData } from "features/paymentMethods/selectors";
import type { PaymentMethod, PaymentMethodsResponse } from "features/paymentMethods/types";
import PaymentMethodValueTooltip from "./PaymentMethodValueTooltip";

const usePaymentMethods = () => {
    const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<PaymentMethodsResponse['data']>();
    const [isEnabledPaymentMethodsLoading, setEnabledPaymentMethodsLoading] = useState(false);

    const paymentMethodsData = useTypedSelector(selectPaymentMethodsData);
    const isPaymentMethodsLoading = useTypedSelector(selectIsPaymentMethodsLoading);

    const request = useFetchResource({
        setLoading: setEnabledPaymentMethodsLoading,
        shouldFetchAll: true,
        perPage: PER_PAGE_SIZE
    });

    const getEnabledPaymentMethods = () => request(queryParams => getPaymentMethods(
        RequestQueryMapper.from(queryParams)
            .containsIn(Filters.isEnabled, `${BooleanSwitchValue.On}`)
            .containsIn(Filters.isSelective, `${BooleanSwitchValue.On}`)
            .toString()
    ));

    const repository = {
        getEnabledPaymentMethods
    };

    const paymentMethodsRepositoryRef = useRef(repository);
    paymentMethodsRepositoryRef.current = repository;

    useEffect(() => {
        const {
            getEnabledPaymentMethods
        } = paymentMethodsRepositoryRef.current;

        getEnabledPaymentMethods()
            .then(({ data }: PaymentMethodsResponse) => setEnabledPaymentMethods(data));
    }, []);

    return paymentMethodsData
        .map(({ tag }: PaymentMethod) => {
            const enabledPaymentMethod = enabledPaymentMethods?.data.find(({ tag: methodTag, isEnabled }) => when(
                Object.is(methodTag, tag),
                isEnabled
            ));

            return {
                name: supportedPaymentMethods[tag as SupportedPaymentMethodsTag]?.displayName ?? capitalize(tag),
                value: convertToBooleanSwitch(Boolean(enabledPaymentMethod)),
                isLoading: isPaymentMethodsLoading || isEnabledPaymentMethodsLoading,
                tooltip: {
                    title: when(
                        enabledPaymentMethod?.isEnabled,
                        null,
                        () => (
                            <PaymentMethodValueTooltip
                                {...enabledPaymentMethod!}
                            />
                        )
                    )
                }
            };
        });
};

export default usePaymentMethods;
